import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Lock, EnvelopeSimple, ArrowRight, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  React.useEffect(() => {
    if (user && typeof user === "object") {
      navigate("/admin", { replace: true });
    }
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back, David.");
      const to = location.state?.from?.pathname || "/admin";
      navigate(to, { replace: true });
    } catch (e2) {
      const msg = formatApiErrorDetail(e2?.response?.data?.detail) || e2.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="login-page" className="ji-container py-20 md:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="ji-overline mb-5">Admin Access</div>
          <h1 style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1.0 }} className="text-5xl md:text-6xl font-bold tracking-tight">
            Sign in to the<br /><span className="text-[var(--ji-accent)] italic">job board.</span>
          </h1>
          <p className="mt-6 text-lg text-[var(--ji-muted)] leading-relaxed max-w-md">
            Review new service requests, update job status, and follow up with customers — all in one place.
          </p>
        </div>

        <div className="lg:col-span-6">
          <form onSubmit={onSubmit} data-testid="login-form" className="ji-card p-7 md:p-10" style={{ borderRadius: 4 }}>
            <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl font-bold tracking-tight">Sign in</h2>
            <p className="text-sm text-[var(--ji-muted)] mt-1">Use your admin credentials.</p>

            <div className="mt-7 space-y-5">
              <div>
                <Label htmlFor="email" className="text-sm font-medium">Email</Label>
                <div className="relative mt-2">
                  <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ji-muted)]" />
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    data-testid="login-email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 pl-9"
                    placeholder="you@josephindustries.com"
                    required
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                <div className="relative mt-2">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ji-muted)]" />
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    data-testid="login-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 pl-9"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>
              {err && (
                <p data-testid="login-error" className="text-sm text-red-600">{err}</p>
              )}
              <button
                type="submit"
                data-testid="login-submit"
                disabled={submitting}
                className="ji-btn-primary w-full inline-flex items-center justify-center gap-2 h-14 text-base font-semibold disabled:opacity-60"
                style={{ borderRadius: 4 }}
              >
                {submitting ? <><Spinner size={18} className="animate-spin" /> Signing in…</> : <>Sign in <ArrowRight size={18} weight="bold" /></>}
              </button>
            </div>

            <div className="mt-6 text-xs text-[var(--ji-muted)] text-center">
              <Link to="/" className="hover:text-[var(--ji-accent)]" data-testid="login-back-home">← Back to website</Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
